import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import type { SlideActionProps } from './types'
import './SlideAction.css'

const TRIGGER_AT = 100
const START_ZONE_PX = 72
const THUMB_INSET_PX = 8

/**
 * Acción deliberada para operaciones físicas: se arrastra el tirador y solo al soltarlo al final se ejecuta.
 * El teclado conserva la misma intención: flechas/Fin avanzan y al soltar la tecla se confirma al 100 %.
 */
export function SlideAction({ label, onComplete, disabled = false, returning = false, className }: SlideActionProps) {
  const [progress, setProgress] = useState(() => (returning ? 100 : 0))
  const [dragging, setDragging] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)
  const pointerStart = useRef(0)
  const progressStart = useRef(0)
  const currentProgress = useRef(returning ? 100 : 0)
  const draggingRef = useRef(false)
  const completed = useRef(false)
  const armed = progress >= TRIGGER_AT && !isComplete && !returning

  useEffect(() => {
    if (!returning) return
    // Deja que el carril completado entre en escena antes de rebobinarlo hacia la izquierda.
    const rewind = window.setTimeout(() => {
      currentProgress.current = 0
      setProgress(0)
    }, 200)
    return () => window.clearTimeout(rewind)
  }, [returning])

  const updateProgress = (next: number) => {
    const bounded = Math.max(0, Math.min(100, next))
    currentProgress.current = bounded
    setProgress(bounded)
  }
  const complete = () => {
    if (completed.current || disabled || currentProgress.current < TRIGGER_AT) return
    completed.current = true
    draggingRef.current = false
    setIsComplete(true)
    setDragging(false)
    updateProgress(100)
    onComplete()
  }
  const reset = () => {
    if (completed.current) return
    draggingRef.current = false
    setDragging(false)
    updateProgress(0)
  }
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled || returning || completed.current || !trackRef.current) return
    const rect = trackRef.current.getBoundingClientRect()
    // La interacción comienza en el tirador, para que tocar la parte final nunca abra una puerta.
    if (event.clientX - rect.left > START_ZONE_PX) return
    event.preventDefault()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    pointerStart.current = event.clientX
    progressStart.current = currentProgress.current
    draggingRef.current = true
    setDragging(true)
  }
  const updateFromPointer = (event: PointerEvent<HTMLDivElement>) => {
    if (!trackRef.current) return
    const usableWidth = Math.max(1, trackRef.current.getBoundingClientRect().width - START_ZONE_PX)
    updateProgress(progressStart.current + ((event.clientX - pointerStart.current) / usableWidth) * 100)
  }
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    updateFromPointer(event)
  }
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    // El último punto puede llegar solo con pointerup (p. ej., si se suelta muy rápido).
    updateFromPointer(event)
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    const reachedEnd = trackRef.current !== null && event.clientX >= trackRef.current.getBoundingClientRect().right
    if (currentProgress.current >= TRIGGER_AT || reachedEnd) {
      currentProgress.current = 100
      complete()
    }
    else reset()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || returning || completed.current) return
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault()
      updateProgress(currentProgress.current + 25)
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault()
      updateProgress(currentProgress.current - 25)
    } else if (event.key === 'Home') {
      event.preventDefault()
      updateProgress(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      updateProgress(100)
    }
  }
  const onKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (['ArrowRight', 'ArrowUp', 'End'].includes(event.key)) complete()
  }

  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={disabled || returning ? -1 : 0}
      aria-label={typeof label === 'string' ? label : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      aria-valuetext={typeof label === 'string' ? label : undefined}
      aria-disabled={disabled || returning || undefined}
      className={cx('hp-slide-action', returning && 'hp-slide-action--returning', progress > 0 && 'hp-slide-action--progress', armed && 'hp-slide-action--armed', dragging && 'hp-slide-action--dragging', isComplete && 'hp-slide-action--complete', disabled && 'hp-slide-action--disabled', className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={reset}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      <span className="hp-slide-action__fill" style={{ width: `${progress}%` }} />
      <span className="hp-slide-action__label" aria-hidden="true">{label}</span>
      <span className="hp-slide-action__ready" aria-hidden="true"><Icon name="lock-open" size={28} /></span>
      <span
        className="hp-slide-action__thumb"
        style={{
          left: `${progress}%`,
          // Mantiene el tirador separado de los dos extremos del carril, incluso al completarlo.
          transform: `translateX(calc(-${progress}% + ${THUMB_INSET_PX - progress * THUMB_INSET_PX * 2 / 100}px))`,
        }}
        aria-hidden="true"
      >
        <Icon name="chevron-right" size={24} />
      </span>
    </div>
  )
}
